import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listEvents } from '../utils/eventsApi';
import { listMatches } from '../utils/matchesApi';
import { FiArrowRight, FiUsers, FiCalendar, FiActivity, FiChevronRight, FiLoader } from 'react-icons/fi';
import { MdLocationOn } from 'react-icons/md';
import AppIcon from './icons/AppIcon';
import '../styles/Home.css';

const getRelativeTime = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Aujourd\'hui';
  if (days === 1) return 'Hier';
  if (days < 7) return `Il y a ${days} j.`;
  if (days < 30) return `Il y a ${Math.floor(days / 7)} sem.`;
  return `Il y a ${Math.floor(days / 30)} mois`;
};

const getEventStatus = (event) => {
  const now = new Date();
  const eventDate = new Date(event.date);
  const eventDateTime = event.startTime ? new Date(event.date + 'T' + event.startTime) : eventDate;
  const eventEndTime = event.endTime ? new Date(event.date + 'T' + event.endTime) : new Date(eventDateTime.getTime() + 2 * 60 * 60 * 1000);
  
  if (now > eventEndTime) {
    return { status: 'Terminé', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' };
  } else if (now >= eventDateTime && now <= eventEndTime) {
    return { status: 'En cours', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.1)' };
  } else if (event.maxParticipants > 0 && event.participants?.length >= event.maxParticipants) {
    return { status: 'Complet', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
  } else {
    return { status: 'Ouvert', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' };
  }
};

export default function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use centralized request function from api.js for consistency
      const [matchesData, eventsData] = await Promise.all([
        listMatches(),
        listEvents()
      ]);
      
      setMatches(matchesData || []);
      setEvents(eventsData || []);
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      // Don't block the UI - show empty state instead
      setMatches([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Show content even while loading - just show empty state if no data
  const showLoading = loading && matches.length === 0 && events.length === 0;
  
  if (currentUser) {
    const userFirstName = currentUser.displayName?.split(' ')[0] || currentUser.email?.split('@')[0] || 'Champion';
    const userStats = {
      matches: matches.length,
      events: events.length,
      participations: [...matches, ...events].filter(m => m.members?.some(member => member._id === currentUser._id) || m.participants?.some(p => p._id === currentUser._id)).length
    };

    return (
      <div style={{ minHeight: '100vh', background: 'transparent' }}>
        {/* Welcome Section - Premium */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(34, 197, 94, 0.06))',
            backdropFilter: 'blur(20px)',
            border: '1.5px solid rgba(56, 189, 248, 0.15)',
            borderRadius: '28px',
            padding: '2.5rem',
            animation: 'slideUpFade 0.6s ease-out both',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h1 style={{ 
                fontSize: 'clamp(2rem, 4vw, 2.5rem)', 
                fontWeight: 900, 
                marginBottom: '0.5rem', 
                background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent', 
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
              }}>
                Bon retour, {userFirstName}!
              </h1>
              
              <p style={{ fontSize: '1.05rem', color: 'var(--muted)', margin: '0 0 1.75rem 0', fontWeight: 500 }}>
                Prêt pour de nouvelles aventures sportives? Découvrez les matchs et événements qui vous attendent.
              </p>
              
              {/* User Statistics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1.25rem',
                marginBottom: '2rem',
                padding: '1.75rem',
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '20px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.5)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: 900, color: 'var(--accent)', marginBottom: '0.5rem' }}>
                    {userStats.matches}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', margin: 0 }}>
                    Matchs
                  </p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: 900, color: '#10b981', marginBottom: '0.5rem' }}>
                    {userStats.events}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', margin: 0 }}>
                    Événements
                  </p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.25rem', fontWeight: 900, color: '#f97316', marginBottom: '0.5rem' }}>
                    {userStats.participations}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', margin: 0 }}>
                    Participations
                  </p>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => navigate('/matches')}
                  style={{
                    padding: '1rem 1.75rem',
                    background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 16px rgba(56, 189, 248, 0.2)',
                    letterSpacing: '-0.3px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(56, 189, 248, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(56, 189, 248, 0.2)';
                  }}
                >
                  <AppIcon name="match" size={20} /> Trouver un match
                </button>
                <button
                  onClick={() => navigate('/create-event')}
                  style={{
                    padding: '1rem 1.75rem',
                    background: 'rgba(56, 189, 248, 0.1)',
                    color: 'var(--accent)',
                    border: '2px solid var(--accent)',
                    borderRadius: '16px',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    letterSpacing: '-0.3px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <AppIcon name="calendar" size={20} /> Créer un événement
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Sections */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem', marginBottom: '3rem' }}>
          
          {/* Matches Section */}
          <section style={{ marginBottom: '3rem', animation: 'slideUpFade 0.6s ease-out 0.2s both' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}><AppIcon name="match" size={24} /> Matchs près de vous</h2>
              <Link to="/matches" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, transition: 'all 0.3s ease', fontSize: '0.95rem' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}>
                Voir tout <FiChevronRight size={18} />
              </Link>
            </div>
            
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: 'var(--muted)' }}>
                <FiLoader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Chargement...</span>
              </div>
            ) : matches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'rgba(56, 189, 248, 0.04)', borderRadius: '20px', border: '1.5px dashed rgba(56, 189, 248, 0.2)' }}>
                <div style={{ marginBottom: '0.75rem' }}><AppIcon name="search" size={40} strokeWidth={1.8} /></div>
                <p style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Aucun match près de vous</p>
                <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>Créez votre premier match ou explorez les événements disponibles</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/create-event')} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>Créer un match</button>
                  <button onClick={() => navigate('/events')} style={{ padding: '0.75rem 1.5rem', background: 'rgba(56, 189, 248, 0.1)', color: 'var(--accent)', border: '2px solid var(--accent)', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)'}>Explorer événements</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {matches.slice(0, 3).map((match, i) => (
                  <div key={match._id || i} onClick={() => {}} style={{ padding: '1.5rem', borderRadius: '20px', background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.05), rgba(56, 189, 248, 0.02))', border: '1.5px solid rgba(56, 189, 248, 0.12)', transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', cursor: 'pointer', animation: `slideUpFade 0.6s ease-out ${0.3 + i * 0.1}s both`, display: 'flex', flexDirection: 'column', height: '100%' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(56, 189, 248, 0.15)'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.12)'; }}>
                    <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2px' }}>{match.date ? new Date(match.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' }) : new Date().toLocaleDateString()}</p>
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text)', lineHeight: 1.3 }}>{match.title || match.sport || 'Match'}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}><MdLocationOn size={16} />{match.location || 'Lieu à définir'}</div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent)', fontWeight: 600, fontSize: '0.9rem' }}><FiUsers size={16} />{match.members?.length || 0} {match.members?.length === 1 ? 'joueur' : 'joueurs'}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Events Section */}
          <section style={{ marginBottom: '3rem', animation: 'slideUpFade 0.6s ease-out 0.4s both' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}><AppIcon name="calendar" size={24} /> Événements populaires</h2>
              <Link to="/events" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, transition: 'all 0.3s ease', fontSize: '0.95rem' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}> 
                Voir tout <FiChevronRight size={18} />
              </Link>
            </div>
            
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: 'var(--muted)' }}>
                <FiLoader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Chargement...</span>
              </div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'rgba(16, 185, 129, 0.04)', borderRadius: '20px', border: '1.5px dashed rgba(16, 185, 129, 0.2)' }}>
                <div style={{ marginBottom: '0.75rem' }}><AppIcon name="target" size={40} strokeWidth={1.8} /></div>
                <p style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1rem' }}>Aucun événement disponible</p>
                <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>Soyez le premier à créer un événement!</p>
                <button onClick={() => navigate('/create-event')} style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>Créer un événement</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {events.slice(0, 3).map((event, i) => {
                  const status = getEventStatus(event);
                  return (
                    <div key={event._id || i} onClick={() => {}} style={{ padding: '1.5rem', borderRadius: '20px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(16, 185, 129, 0.02))', border: '1.5px solid rgba(16, 185, 129, 0.12)', transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', cursor: 'pointer', animation: `slideUpFade 0.6s ease-out ${0.5 + i * 0.1}s both`, display: 'flex', flexDirection: 'column', height: '100%' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(16, 185, 129, 0.15)'; e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.12)'; }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2px', margin: 0 }}>{event.date ? new Date(event.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' }) : 'Date TBD'}</p>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.35rem 0.75rem', borderRadius: '8px', background: status.bg, color: status.color }}>{status.status}</span>
                      </div>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text)', lineHeight: 1.3 }}>{event.sportType || 'Événement'}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}><MdLocationOn size={16} />{event.location || 'Lieu TBD'}</div>
                      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontWeight: 600, fontSize: '0.9rem' }}><FiUsers size={16} />{event.participants?.length || 0} {event.participants?.length === 1 ? 'participant' : 'participants'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Recent Activity Section */}
          <section style={{ animation: 'slideUpFade 0.6s ease-out 0.6s both' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', margin: '0 0 1.5rem 0' }}><AppIcon name="activity" size={24} /> Activité récente</h2>
            
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: 'var(--muted)' }}>
                <FiLoader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Chargement...</span>
              </div>
            ) : (matches.length > 0 || events.length > 0) ? (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {[...matches.map((m) => ({ type: 'match', title: m.title, date: m.date, sport: m.sport, id: m._id })), ...events.map((e) => ({ type: 'event', title: e.sportType, date: e.date, sport: e.sportType, id: e._id }))]
                  .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                  .slice(0, 3)
                  .map((item, i) => (
                    <div key={item.id || i} style={{ padding: '1.25rem', borderRadius: '16px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.04)', display: 'flex', alignItems: 'center', gap: '1rem', transition: 'all 0.3s ease', animation: `slideUpFade 0.6s ease-out ${0.7 + i * 0.1}s both`, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'; e.currentTarget.style.transform = 'translateX(4px)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'; e.currentTarget.style.transform = 'translateX(0)'; }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: item.type === 'match' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'linear-gradient(135deg, #10b981, #047857)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FiActivity size={24} color="white" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.type === 'match' ? `⚽ ${item.title}` : `📅 ${item.title}`}
                        </p>
                        <p style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>{getRelativeTime(item.date)}</p>
                      </div>
                      <FiChevronRight size={20} color="var(--muted)" style={{ flexShrink: 0 }} />
                    </div>
                  ))
                }
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>Aucune activité récente</div>
            )}
          </section>
        </div>
      </div>
    );
  }

  // Non-authenticated user - Pre-login hero section
  return (
    <main className="home-hero">
      <div className="home-hero__inner">
        <div className="home-hero__visual" aria-hidden="true">
          <div className="match-network">
            <div className="match-network__halo match-network__halo--outer" />
            <div className="match-network__halo match-network__halo--inner" />
            <svg className="match-network__lines" viewBox="0 0 520 520" role="presentation">
              <path d="M105 158 C180 90 235 145 260 260 S360 420 428 355" />
              <path d="M105 158 C155 250 195 355 260 260 S335 130 410 145" />
              <path d="M115 365 C180 400 210 295 260 260 S380 250 410 145" />
              <path d="M115 365 C180 300 190 180 260 260 S340 380 428 355" />
            </svg>
            <span className="match-network__orbit match-network__orbit--one" />
            <span className="match-network__orbit match-network__orbit--two" />
            <span className="player-dot player-dot--one"><i /></span>
            <span className="player-dot player-dot--two"><i /></span>
            <span className="player-dot player-dot--three"><i /></span>
            <span className="player-dot player-dot--four"><i /></span>
            <div className="match-core">
              <div className="match-core__icon"><AppIcon name="match" size={30} strokeWidth={1.5} /></div>
              <strong>GO<span>PLAY</span></strong>
              <small>MATCH EN COURS</small>
            </div>
            <div className="hero-floating-card hero-floating-card--top">
              <span className="hero-card__icon"><AppIcon name="match" size={14} /></span>
              <span><strong>Football</strong><small>4 / 6 joueurs</small></span>
            </div>
            <div className="hero-floating-card hero-floating-card--side">
              <span className="hero-card__status" />
              <span><strong>Match trouvé</strong><small>À 1.2 km</small></span>
            </div>
            <div className="hero-floating-card hero-floating-card--bottom">
              <span className="hero-card__metric">18:30</span>
              <span><strong>2 places</strong><small>disponibles</small></span>
            </div>
          </div>
        </div>

        <div className="home-hero__content">
          <span className="home-hero__eyebrow"><span /> RÉSEAU GOPLAY</span>
          <h1>
            Prêt pour un <span>nouveau match?</span>
          </h1>
          <p className="home-hero__description">
            Découvrez les meilleurs matchs près de chez vous. Rejoignez une communauté de joueurs passionnés et améliorez votre jeu.
          </p>
          <div className="home-hero__actions">
            <Link to="/register" className="home-hero__button home-hero__button--primary">
              Commencer <FiArrowRight size={20} />
            </Link>
            <Link to="/login" className="home-hero__button home-hero__button--secondary">
              Se connecter
            </Link>
          </div>
          <div className="home-hero__community">
            <div className="home-hero__avatars" aria-hidden="true"><span>LM</span><span>AK</span><span>SD</span><span>+</span></div>
            <span><AppIcon name="check" size={16} /> Rejoignez 5,000+ joueurs actifs</span>
          </div>
        </div>
      </div>
    </main>
  );
}
